import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import Button from '@sorare/core/src/atoms/buttons/Button';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { useIsDesktopAndAbove } from '@sorare/core/src/hooks/device/useIsDesktopAndAbove';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { BoxOddsDialog } from 'components/rivals/seasons/BoxOddsDialog';
import { SeasonSelector } from 'components/rivals/seasons/SeasonSelector';
import { ScrollableDivisionPicker } from 'components/rivals/seasons/divisionPicker/ScrollableDivisionPicker';

import {
  SeasonLeaderboardHeader_footballRivalsDivisionLeaderboard,
  SeasonLeaderboardHeader_footballRivalsDivisionLeaderboardConfig,
} from './__generated__/index.graphql';

const Root = styled(Vertical)`
  isolation: isolate;
`;
const PickerWrapper = styled.div`
  z-index: 1;
  padding: var(--unit) var(--double-unit) var(--double-unit);
`;
const DesktopWrapper = styled(SBHorizontal).attrs({ gap: 2 })`
  z-index: 1;
  padding: var(--unit) var(--double-unit) var(--double-unit);
`;

type Props = {
  configs: SeasonLeaderboardHeader_footballRivalsDivisionLeaderboardConfig[];
  seasonSlug: string;
  userSlug: string;
  onSeasonChange: (seasonSlug: string) => void;
  leaderboard?: Nullable<SeasonLeaderboardHeader_footballRivalsDivisionLeaderboard>;
};
export const SeasonLeaderboardHeader = ({
  configs,
  seasonSlug,
  userSlug,
  onSeasonChange,
  leaderboard,
}: Props) => {
  const isDesktopAndAbove = useIsDesktopAndAbove();
  const [oddsDialogOpen, setOddsDialogOpen] = useState(false);
  const defaultDivision =
    leaderboard?.leaderboardConfig?.divisionConfig ||
    configs.at(-1)?.divisionConfig;
  const [selectedDivision, setSelectedDivision] = useState(defaultDivision);
  const divisionConfigs = configs.map(({ divisionConfig }) => divisionConfig);

  return (
    <Root gap={2}>
      {isDesktopAndAbove ? (
        <DesktopWrapper>
          <SeasonSelector
            seasonSlug={seasonSlug}
            userSlug={userSlug}
            onChange={onSeasonChange}
          />
          <Button
            size="small"
            color="tertiary"
            onClick={() => {
              setOddsDialogOpen(true);
            }}
          >
            <FormattedMessage {...fantasy.viewPrizePool} />
          </Button>
        </DesktopWrapper>
      ) : (
        <>
          <PickerWrapper>
            <ScrollableDivisionPicker
              selectedDivision={selectedDivision}
              divisionConfigs={divisionConfigs}
              onClick={division => {
                setSelectedDivision(division);
                setOddsDialogOpen(true);
              }}
              biggerSelected
            />
          </PickerWrapper>
          <Horizontal center>
            <SeasonSelector
              seasonSlug={seasonSlug}
              userSlug={userSlug}
              onChange={onSeasonChange}
            />
          </Horizontal>
        </>
      )}

      {oddsDialogOpen && selectedDivision && (
        <BoxOddsDialog
          open
          leaderboardsConfig={configs}
          initiallySelectedDivision={selectedDivision}
          initiallySelectedRank={1}
          onClose={() => {
            setOddsDialogOpen(false);
            setSelectedDivision(defaultDivision);
          }}
        />
      )}
    </Root>
  );
};

SeasonLeaderboardHeader.fragments = {
  footballRivalsDivisionLeaderboardConfig: gql`
    fragment SeasonLeaderboardHeader_footballRivalsDivisionLeaderboardConfig on FootballRivalsDivisionLeaderboardConfig {
      divisionConfig {
        id
        title
        ...ScrollableDivisionPicker_footballRivalsDivisionLeaderboardDivisionConfig
      }
      ...BoxOddsDialog_footballRivalsDivisionLeaderboardConfig
    }
    ${ScrollableDivisionPicker.fragments
      .footballRivalsDivisionLeaderboardDivisionConfig}
    ${BoxOddsDialog.fragments.footballRivalsDivisionLeaderboardConfig}
  ` as TypedDocumentNode<SeasonLeaderboardHeader_footballRivalsDivisionLeaderboardConfig>,
  footballRivalsDivisionLeaderboard: gql`
    fragment SeasonLeaderboardHeader_footballRivalsDivisionLeaderboard on FootballRivalsDivisionLeaderboard {
      id
      leaderboardConfig {
        divisionConfig {
          id
        }
        ...BoxOddsDialog_footballRivalsDivisionLeaderboardConfig
      }
    }
    ${BoxOddsDialog.fragments.footballRivalsDivisionLeaderboardConfig}
  ` as TypedDocumentNode<SeasonLeaderboardHeader_footballRivalsDivisionLeaderboard>,
};
